import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@fetmode/i18n';
import { formatCoins } from '@fetmode/utils';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';
import { TIP_PRESETS } from './TipModal.js';

export interface ShowEndFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  canTip: boolean;
  coinBalance: number;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (input: {
    rating: number | null;
    comment: string;
    tipCoins: number | null;
  }) => Promise<void>;
}

export function ShowEndFeedbackModal({
  isOpen,
  onClose,
  canTip,
  coinBalance,
  isSubmitting,
  error,
  onSubmit,
}: ShowEndFeedbackModalProps): ReactElement | null {
  const { requireCoins } = useCoinShop();
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [tipCoins, setTipCoins] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setRating(null);
    setComment('');
    setTipCoins(null);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) {
    return null;
  }

  const canSendTip = canTip && tipCoins !== null && tipCoins <= coinBalance;
  const canSubmit = rating !== null || canSendTip;

  return createPortal(
    <div
      className="show-room-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-show-feedback-title"
    >
      <button
        type="button"
        className="show-room-modal__backdrop"
        aria-label={t('common.show.endShowFeedbackSkip')}
        onClick={() => {
          if (!isSubmitting) {
            onClose();
          }
        }}
      />
      <div className="show-room-modal__panel">
        <header className="show-room-modal__header">
          <h2 id="end-show-feedback-title" className="show-room-modal__title">
            {t('common.show.endShowFeedbackTitle')}
          </h2>
          <button
            type="button"
            className="show-room-modal__close"
            onClick={() => {
              if (!isSubmitting) {
                onClose();
              }
            }}
            aria-label={t('common.show.endShowFeedbackSkip')}
          >
            ×
          </button>
        </header>
        <div className="show-room-modal__body">
          <p className="show-room-modal__muted" style={{ marginTop: 0 }}>
            {t('common.show.endShowFeedbackHint')}
          </p>

          <section className="show-room-modal__section" aria-label={t('common.show.endShowFeedbackTitle')}>
            <div className="show-room-feedback__stars">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`show-room-feedback__star${rating !== null && rating >= value ? ' is-active' : ''}`}
                  aria-label={t('common.show.endShowFeedbackStar').replace('{{count}}', String(value))}
                  aria-pressed={rating === value}
                  disabled={isSubmitting}
                  onClick={() => {
                    setRating(value);
                  }}
                >
                  ★
                </button>
              ))}
            </div>
          </section>

          <label className="show-room-modal__schedule-field" style={{ marginTop: '0.75rem' }}>
            {t('common.show.endShowFeedbackComment')}
            <textarea
              className="fm-input"
              rows={3}
              maxLength={500}
              value={comment}
              disabled={isSubmitting}
              placeholder={t('common.show.endShowFeedbackCommentPlaceholder')}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
          </label>

          {canTip ? (
            <section className="show-room-modal__section">
              <h3 className="show-room-modal__section-title">{t('common.show.endShowFeedbackTip')}</h3>
              <div className="show-room-modal__duration-grid">
                {TIP_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`show-room-modal__duration${tipCoins === preset ? ' show-room-modal__duration--active' : ''}`}
                    disabled={isSubmitting}
                    onClick={() => {
                      if (tipCoins === preset) {
                        setTipCoins(null);
                        return;
                      }
                      if (!requireCoins(coinBalance, preset, () => {
                        setTipCoins(preset);
                      })) {
                        return;
                      }
                      setTipCoins(preset);
                    }}
                  >
                    <strong>{formatCoins(preset)}</strong>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {error ? (
            <p className="fm-error" style={{ marginTop: '0.75rem' }}>
              {error}
            </p>
          ) : null}

          <div className="show-room-feedback__actions">
            <Button
              label={t('common.show.endShowFeedbackSkip')}
              variant="ghost"
              disabled={isSubmitting}
              onClick={onClose}
            />
            <Button
              label={t('common.show.endShowFeedbackSubmit')}
              variant="primary"
              disabled={isSubmitting || !canSubmit}
              onClick={() => {
                void onSubmit({
                  rating,
                  comment: comment.trim(),
                  tipCoins: canSendTip ? tipCoins : null,
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
